import type {Context} from '../types';

const NEEDED_NODE_TYPES = new Set<string>([
  'CommerceAddToCartButton',
  'CommerceBuyNowButton',
  'CommerceCartErrorMsg',
  'CommerceAddToCartErrorMsg',
  'CommerceCheckoutErrorMsg',
]);

export default (context: Context) => {
  const {
    node: {jsonNode},
  } = context;

  const {data = {}} = jsonNode;

  const isAlreadyMigrated =
    Array.isArray(jsonNode?.data?.texts) && Boolean(data.texts.length);

  const needsMigration =
    !isAlreadyMigrated && jsonNode.type && NEEDED_NODE_TYPES.has(jsonNode.type);

  return {
    needed: Boolean(needsMigration),
    logData: {
      msg: `Migrating ${jsonNode.type} node data shape`,
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      if (jsonNode.type === 'CommerceAddToCartButton') {
        migrateCommerceAddToCartButtonNode(jsonNode);
      } else if (jsonNode.type === 'CommerceBuyNowButton') {
        migrateCommerceBuyNowButtonNode(jsonNode);
      } else if (jsonNode.type === 'CommerceCartErrorMsg') {
        migrateCommerceCartErrorMsgNode(jsonNode);
      } else if (jsonNode.type === 'CommerceAddToCartErrorMsg') {
        migrateCommerceAddToCartErrorMsgNode(jsonNode);
      } else if (jsonNode.type === 'CommerceCheckoutErrorMsg') {
        migrateCommerceCheckoutErrorMsgNode(jsonNode);
      }
    },
  };
};

const deleteLegacyCommerceData = (
  jsonNode: any,
  copy: Record<string, string>
) => {
  if (jsonNode.data) {
    if (jsonNode.data.commerce) {
      for (const [key, value] of Object.entries(jsonNode.data.commerce)) {
        if (
          key in copy &&
          typeof value === 'string' &&
          value.replace(/\s\s+/g, ' ') !== copy[key]
        ) {
          copy[key] = value;
        }
      }

      for (const key of Object.keys(jsonNode.data.commerce)) {
        if (key in copy) {
          // eslint-disable-next-line webflow/no-delete
          delete jsonNode.data.commerce[key];
        }
      }

      if (Object.keys(jsonNode.data.commerce).length === 0) {
        // eslint-disable-next-line webflow/no-delete
        delete jsonNode.data.commerce;
      }
    }

    if (jsonNode.data.textStateCopy) {
      for (const [key, value] of Object.entries(jsonNode.data.textStateCopy)) {
        copy[key] = value as string;
      }
      // eslint-disable-next-line webflow/no-delete
      delete jsonNode.data.textStateCopy;
    }
  }
};

const migrateCommerceAddToCartButtonNode = (jsonNode: any) => {
  const copy = {
    DEFAULT: 'Add to Cart',
    WAITING: 'Adding to cart...',
  };

  deleteLegacyCommerceData(jsonNode, copy);

  const newData = {
    ...jsonNode.data,
    temp: {index: 0},
    texts: [
      {
        id: 'COMMERCE_ADD_TO_CART_BUTTON_DEFAULT',
        name: 'Default text',
        copy: copy.DEFAULT,
        category: 'Button text',
      },
      {
        id: 'COMMERCE_ADD_TO_CART_BUTTON_WAITING',
        name: 'Waiting text',
        copy: copy.WAITING,
        category: 'Button text',
      },
    ],
  };

  jsonNode.data = newData;
};

const migrateCommerceBuyNowButtonNode = (jsonNode: any) => {
  const copy = {
    DEFAULT: 'Buy now',
    SUBSCRIPTION: 'Subscribe now',
  };

  deleteLegacyCommerceData(jsonNode, copy);

  const newData = {
    ...jsonNode.data,
    temp: {index: 0},
    texts: [
      {
        id: 'COMMERCE_BUY_NOW_BUTTON_DEFAULT',
        name: 'Default text',
        copy: copy.DEFAULT,
        category: 'Button text',
      },
      {
        id: 'SUBSCRIPTION_BUTTON_DEFAULT',
        name: 'Subscription text',
        copy: copy.SUBSCRIPTION,
        category: 'Button text',
      },
    ],
  };

  jsonNode.data = newData;
};

const migrateCommerceCartErrorMsgNode = (jsonNode: any) => {
  const copy = {
    QUANTITY_ERROR: 'Product is not available in this quantity.',
    GENERAL_ERROR: 'Something went wrong when adding this item to the cart.',
    CHECKOUT_ERROR: 'Checkout is disabled on this site.',
    CART_ORDER_MIN_ERROR:
      'The order minimum was not met. Add more items to your cart to continue.',
    SUBSCRIPTION_ERR:
      'Before you purchase, please use your email invite to verify your address so we can send order updates.',
  };

  deleteLegacyCommerceData(jsonNode, copy);

  const newData = {
    ...jsonNode.data,
    temp: {index: 0},
    texts: [
      {
        id: 'QUANTITY_ERROR',
        name: 'Quantity not available',
        copy: copy.QUANTITY_ERROR,
        category: 'General Errors',
      },
      {
        id: 'GENERAL_ERROR',
        name: 'General error',
        copy: copy.GENERAL_ERROR,
        category: 'General Errors',
      },
      {
        id: 'CHECKOUT_ERROR',
        name: 'Checkout disabled',
        copy: copy.CHECKOUT_ERROR,
        category: 'General Errors',
      },
      {
        id: 'CART_ORDER_MIN_ERROR',
        name: 'Order minimum not met',
        copy: copy.CART_ORDER_MIN_ERROR,
        category: 'Billing Errors',
      },
      {
        id: 'SUBSCRIPTION_ERR',
        name: 'Subscription not verified',
        copy: copy.SUBSCRIPTION_ERR,
        category: 'Subscription Errors',
      },
    ],
  };

  jsonNode.data = newData;
};

const migrateCommerceAddToCartErrorMsgNode = (jsonNode: any) => {
  const copy = {
    ADD_TO_CART_QUANTITY_ERROR: 'Product is not available in this quantity.',
    ADD_TO_CART_GENERAL_ERROR:
      'Something went wrong when adding this item to the cart.',
    ADD_TO_CART_MIXED_ERROR:
      'You can’t purchase another product with a subscription.',
    BUY_NOW_ERROR: 'Something went wrong when trying to purchase this item.',
    CHECKOUT_DISABLED_ERROR: 'Checkout is disabled on this site.',
    SELECT_ALL_OPTIONS: 'Please select an option in each set.',
  };

  deleteLegacyCommerceData(jsonNode, copy);

  const newData = {
    ...jsonNode.data,
    temp: {index: 0},
    tag: 'label',
    text: true,
    texts: [
      {
        id: 'ADD_TO_CART_QUANTITY_ERROR',
        name: 'Quantity not available',
        copy: copy.ADD_TO_CART_QUANTITY_ERROR,
        category: 'General Errors',
      },
      {
        id: 'ADD_TO_CART_GENERAL_ERROR',
        name: 'Add to Cart error',
        copy: copy.ADD_TO_CART_GENERAL_ERROR,
        category: 'General Errors',
      },
      {
        id: 'ADD_TO_CART_MIXED_ERROR',
        name: 'Add to mixed Cart error',
        copy: copy.ADD_TO_CART_MIXED_ERROR,
        category: 'General Errors',
      },
      {
        id: 'BUY_NOW_ERROR',
        name: 'Buy now error',
        copy: copy.BUY_NOW_ERROR,
        category: 'General Errors',
      },
      {
        id: 'CHECKOUT_DISABLED_ERROR',
        name: 'Checkout disabled',
        copy: copy.CHECKOUT_DISABLED_ERROR,
        category: 'General Errors',
      },
      {
        id: 'SELECT_ALL_OPTIONS',
        name: 'Option selection required',
        copy: copy.SELECT_ALL_OPTIONS,
        category: 'General Errors',
      },
    ],
  };

  jsonNode.data = newData;
};

const migrateCommerceCheckoutErrorMsgNode = (jsonNode: any) => {
  const copy = {
    INFO_ERROR:
      'There was an error processing your customer info. Please try again, or contact us if you continue to have problems.',
    SHIPPING_ERROR: 'Sorry. We can’t ship your order to the address provided.',
    ORDER_EXTRAS_ERROR:
      'A merchant setting has changed that impacts your cart. Please refresh and try again.',
    PRICING_ERROR:
      'The prices of one or more items in your cart have changed. Please refresh this page and try again.',
    PRODUCT_ERROR:
      'One or more of the products in your cart have been removed. Please refresh the page and try again.',
    PAYMENT_ERROR:
      'There was an error processing your payment. Please try again, or contact us if you continue to have problems.',
    BILLING_ERROR:
      'Your payment could not be completed with the payment information provided. Please make sure that your card and billing address information is correct, or try a different payment card, to complete this order. Contact us if you continue to have problems.',
    ORDER_MINIMUM_ERROR:
      'The order minimum was not met. Add more items to your cart to continue.',
    INVALID_DISCOUNT_ERROR: 'This discount is invalid.',
    EXPIRED_DISCOUNT_ERROR: 'This discount is no longer available.',
    USAGE_REACHED_DISCOUNT_ERROR: 'This discount is no longer available.',
    REQUIREMENTS_NOT_MET_DISCOUNT_ERROR:
      'Your order does not meet the requirements for this discount.',
  };

  deleteLegacyCommerceData(jsonNode, copy);

  const newData = {
    ...jsonNode.data,
    temp: {index: 0},
    tag: 'label',
    text: true,
    texts: [
      {
        id: 'INFO_ERROR',
        name: 'General customer info error',
        copy: copy.INFO_ERROR,
        category: 'General Errors',
      },
      {
        id: 'SHIPPING_ERROR',
        name: 'Shipping not available',
        copy: copy.SHIPPING_ERROR,
        category: 'General Errors',
      },
      {
        id: 'ORDER_EXTRAS_ERROR',
        name: 'Merchant setting changed',
        copy: copy.ORDER_EXTRAS_ERROR,
        category: 'General Errors',
      },
      {
        id: 'PRICING_ERROR',
        name: 'Product price changed',
        copy: copy.PRICING_ERROR,
        category: 'Product Errors',
      },
      {
        id: 'PRODUCT_ERROR',
        name: 'Product removed',
        copy: copy.PRODUCT_ERROR,
        category: 'Product Errors',
      },
      {
        id: 'PAYMENT_ERROR',
        name: 'General payment error',
        copy: copy.PAYMENT_ERROR,
        category: 'Billing Errors',
      },
      {
        id: 'BILLING_ERROR',
        name: 'Card declined',
        copy: copy.BILLING_ERROR,
        category: 'Billing Errors',
      },
      {
        id: 'ORDER_MINIMUM_ERROR',
        name: 'Order minimum not met',
        copy: copy.ORDER_MINIMUM_ERROR,
        category: 'Billing Errors',
      },
      {
        id: 'INVALID_DISCOUNT_ERROR',
        name: 'Invalid discount error',
        copy: copy.INVALID_DISCOUNT_ERROR,
        category: 'Discount Errors',
      },
      {
        id: 'EXPIRED_DISCOUNT_ERROR',
        name: 'Discount expired',
        copy: copy.EXPIRED_DISCOUNT_ERROR,
        category: 'Discount Errors',
      },
      {
        id: 'USAGE_REACHED_DISCOUNT_ERROR',
        name: 'Discount usage limit reached',
        copy: copy.USAGE_REACHED_DISCOUNT_ERROR,
        category: 'Discount Errors',
      },
      {
        id: 'REQUIREMENTS_NOT_MET_DISCOUNT_ERROR',
        name: 'Discount requirements not met',
        category: 'Discount Errors',
        copy: copy.REQUIREMENTS_NOT_MET_DISCOUNT_ERROR,
      },
    ],
  };

  jsonNode.data = newData;
};
